import React, { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useRest, RCard, RHead, RestLoading, RestPageBrief, SourceBadge, money, money2, BRAND, BrandMark, RestReconnect } from './restui';
import { KxRoiStrip } from './KxRoi';

export default function RestDelivery() {
  const { isEs } = useOutletContext();
  const [days, setDays] = useState(30);
  const { data: d, err, loading } = useRest(`/delivery?days=${days}`);
  if (loading && !d) return <RestLoading />;
  if (err) return <RestReconnect />;
  const rl = d.revenue_link || {};
  const platforms = [...(d.platforms || [])].sort((a, b) => b.total - a.total);
  const deliveryOnly = platforms.filter((p) => p.key !== 'direct');
  const top = deliveryOnly[0];
  const topDeliveryShare = top && rl.delivery_total ? Math.round(100 * top.total / rl.delivery_total) : 0;
  return (
    <div data-testid="rest-delivery">
      <RHead title="Delivery"
        sub={isEs ? 'Rendimiento por plataforma — DoorDash, Uber Eats, Grubhub y pedido directo, ligado a tu revenue.' : 'Performance by platform — DoorDash, Uber Eats, Grubhub and direct ordering, tied to your revenue.'}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="rest-delivery-range"
              className="h-9 px-3 rounded-full border border-gray-200 bg-white text-[13px] font-semibold text-gray-600 focus:outline-none cursor-pointer">
              {[[7, isEs ? '7 días' : '7 days'], [30, isEs ? '30 días' : '30 days'], [90, isEs ? '90 días' : '90 days'], [365, isEs ? '12 meses' : '12 months']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <SourceBadge source={d.has_data ? 'manual_imports' : 'not_connected'} isEs={isEs} />
          </div>
        } />
      <KxRoiStrip isEs={isEs} />
      {!d.has_data ? (
        <RCard className="p-10 text-center">
          <p className="text-gray-500 text-[14px]">{isEs ? 'Sube un reporte de DoorDash / Uber Eats / Grubhub en Reports para activar este módulo. Sin datos reales no mostramos nada.' : 'Upload a DoorDash / Uber Eats / Grubhub export in Reports to activate this module. No real data, nothing shown.'}</p>
        </RCard>
      ) : (
        <>
          {top && (
            <RestPageBrief isEs={isEs} testid="rest-delivery-brief"
              summary={isEs
                ? `Tu canal líder de delivery es ${(BRAND[top.key] || {}).label || top.key} con el ${topDeliveryShare}% del delivery. En conjunto, el delivery aporta ${money(rl.delivery_total)} — el ${rl.delivery_pct}% de tus ventas totales del periodo (${money(rl.total_revenue)}).`
                : `Your leading delivery channel is ${(BRAND[top.key] || {}).label || top.key} at ${topDeliveryShare}% of delivery. Combined, delivery brings ${money(rl.delivery_total)} — ${rl.delivery_pct}% of your total sales this period (${money(rl.total_revenue)}).`}
              alert={topDeliveryShare > 55 ? (isEs
                ? `Dependencia alta de ${(BRAND[top.key] || {}).label || top.key} (${topDeliveryShare}% del delivery) — impulsa el pedido directo en tu web para proteger el margen.`
                : `High dependence on ${(BRAND[top.key] || {}).label || top.key} (${topDeliveryShare}% of delivery) — push direct web ordering to protect margin.`) : null}
              kpi={{ label: isEs ? 'Delivery total' : 'Delivery total', value: money(rl.delivery_total), sub: `${rl.delivery_pct}% ${isEs ? 'de tus ventas' : 'of your sales'}` }} />
          )}

          {/* Delivery ↔ Revenue link */}
          <RCard className="p-4 sm:p-5 mb-4" testid="rest-delivery-revlink">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.15em] text-gray-400 font-bold">{isEs ? 'Ventas totales del periodo' : 'Total sales this period'}</p>
                <p className="text-[20px] font-bold tabular-nums text-gray-900">{money(rl.total_revenue)}</p>
              </div>
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.15em] text-gray-400 font-bold">Delivery</p>
                <p className="text-[20px] font-bold tabular-nums text-gray-900">{money(rl.delivery_total)}</p>
              </div>
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.15em] text-gray-400 font-bold">{isEs ? '% del total' : '% of total'}</p>
                <p className="text-[20px] font-bold tabular-nums text-[var(--rest-accent)]">{rl.delivery_pct}%</p>
              </div>
              <div className="flex-1 min-w-[160px]">
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full kx-grow-x bg-[var(--rest-accent)]" style={{ width: `${Math.min(100, rl.delivery_pct)}%` }} />
                </div>
                <p className="mt-1 text-[10.5px] text-gray-400">{isEs ? 'Delivery vs ventas totales' : 'Delivery vs total sales'}</p>
              </div>
              <Link to="/restaurant/revenue" className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[var(--rest-accent)] hover:underline" data-testid="rest-delivery-to-revenue">
                {isEs ? 'Ver Revenue' : 'View Revenue'} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </RCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {platforms.map((p) => {
              const b = BRAND[p.key] || { label: p.key, color: '#9ca3af' };
              return (
                <RCard key={p.key} className="p-6 relative overflow-hidden" testid={`rest-delivery-${p.key}`}>
                  <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: b.color }} aria-hidden="true" />
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-2.5">
                      <BrandMark k={p.key} size={20} />
                      <p className="text-[15px] font-bold text-gray-900">{b.label}</p>
                    </span>
                    <span className="text-[12px] font-semibold rounded-full px-2.5 py-1" style={{ background: `${b.color}14`, color: b.color }}>{p.share_pct}% {isEs ? 'del total' : 'of total'}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                    <div><p className="text-[18px] font-semibold tabular-nums text-gray-900">{money(p.total)}</p><p className="text-[10.5px] text-gray-400 uppercase tracking-wider">{isEs ? 'Ventas' : 'Sales'}</p></div>
                    <div><p className="text-[18px] font-semibold tabular-nums text-gray-900">{p.orders.toLocaleString()}</p><p className="text-[10.5px] text-gray-400 uppercase tracking-wider">{isEs ? 'Órdenes' : 'Orders'}</p></div>
                    <div><p className="text-[18px] font-semibold tabular-nums text-gray-900">{money2(p.avg_ticket)}</p><p className="text-[10.5px] text-gray-400 uppercase tracking-wider">Ticket</p></div>
                  </div>
                  <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full kx-grow-x" style={{ width: `${Math.max(3, p.share_pct)}%`, background: b.color }} />
                  </div>
                </RCard>
              );
            })}
          </div>
        </>
      )}
      <RCard className="p-5 mt-5">
        <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">{isEs ? 'APIs directas' : 'Direct APIs'}</p>
        <div className="flex flex-wrap gap-2">
          {Object.keys(d.connected_apis || {}).map((k) => (
            <Link key={k} to="/restaurant/connections" data-testid={`rest-delivery-api-${k}`}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1.5 text-[12px] text-gray-600 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)] transition-colors">
              <BrandMark k={k} size={14} /> {(BRAND[k] || {}).label || k} · {isEs ? 'Conectar →' : 'Connect →'}
            </Link>
          ))}
        </div>
        <p className="mt-3 text-[11.5px] text-gray-400">{isEs ? 'Cuando conectemos las APIs, los datos fluirán automáticamente. Hoy: importación manual, 100% transparente.' : 'Once APIs are connected, data flows automatically. Today: manual imports, 100% transparent.'}</p>
      </RCard>
    </div>
  );
}
